import { createSlice } from '@reduxjs/toolkit';
import { TaxProfile } from '../../@types/TaxProfile';
import { Taxpayer } from '../../@types/taxpayer.type';
import { RappiCertificate, RetentionCertificatesMeta } from '../../@types/FiscalDocuments.type';

interface TaxProfileState {
	links: TaxProfile | null;
	isLoading: boolean;
	userHasRetentions: {
		dateFirstRetention: string;
		dateOldRetention: string;
		hasRetentions: boolean;
	} | null;
	declarationsOverview: any[] | null;
	annualDeclarations: any[] | null;
	selectedDeclaration: any | null;
	isLoadingDeclarations: boolean;
	isLoadingAnnualDeclarations: boolean;
	showNewModalPendingDeclarations: boolean;
	showNewModalPendingDeclarationWithPrecalculate: boolean;
	selectedApprovalDeclaration: any | null;
	showRegularizationFlow: boolean;
	/** Desde dónde se abrió el flujo: viaja al carrito en el sello del
	 * paquete, porque el banner y el gauge abren el MISMO modal y sin esto
	 * la venta no dice cuál de los dos la produjo. */
	regularizationFlowSource: string | null;
	/** Brazo forzado por deeplink de campaña, si lo hay. */
	regularizationFlowArm: string | null;
	/** What the user asked to catch up on. The control step flow only covers
	 * monthly periods; an annual request goes to the builder instead. */
	regularizationFlowScope: 'monthly' | 'annual';
	/**
	 * Brazo del experimento "Ponte al día" ya resuelto, publicado por el host
	 * del modal. Lo leen las superficies que deciden si ofrecer regularización
	 * ANTES de abrir el modal (el gauge del score), porque las anuales solo se
	 * ofrecen en los brazos de paquete.
	 */
	ponteAlDiaArm: string | null;
	taxpayer: Taxpayer | null;
	isLoadingTaxpayer: boolean;
	retentionCertificates: RappiCertificate[] | null;
	/** De dónde salieron los certificados (me | legacy) y cómo se cruzaron (t-147). */
	retentionCertificatesMeta: RetentionCertificatesMeta | null;
	wrappedData: any | null;
}

const initialState: TaxProfileState = {
	links: null,
	isLoading: false,
	userHasRetentions: null,
	declarationsOverview: null,
	showNewModalPendingDeclarations: false,
	showNewModalPendingDeclarationWithPrecalculate: false,
	annualDeclarations: null,
	selectedDeclaration: null,
	selectedApprovalDeclaration: null,
	isLoadingDeclarations: true,
	isLoadingAnnualDeclarations: true,
	showRegularizationFlow: false,
	regularizationFlowSource: null,
	regularizationFlowArm: null,
	regularizationFlowScope: 'monthly',
	ponteAlDiaArm: null,
	taxpayer: null,
	isLoadingTaxpayer: true,
	retentionCertificates: null,
	retentionCertificatesMeta: null,
	wrappedData: null,
};

export const taxProfileSlice = createSlice({
	name: 'taxProfile',
	initialState,
	reducers: {
		setIsLoadingTaxProfile: (state, action) => {
			state.isLoading = action.payload;
		},
		addUserHasRetentions: (state, action) => {
			state.userHasRetentions = action.payload;
		},
		addLinks: (state, action) => {
			state.links = action.payload;
		},
		restartLinks: (state) => {
			state.links = null;
		},
		setDeclarationsOverview: (state, action) => {
			state.declarationsOverview = action.payload;
		},
		setSelectedDeclaration: (state, action) => {
			state.selectedDeclaration = action.payload;
		},
		setIsLoadingDeclarations: (state, action) => {
			state.isLoadingDeclarations = action.payload;
		},
		setIsLoadingAnnualDeclarations: (state, action) => {
			state.isLoadingAnnualDeclarations = action.payload;
		},
		addAnnualDeclarations: (state, action) => {
			state.annualDeclarations = action.payload;
		},
		setShowNewModalPendingDeclarations: (state, action) => {
			state.showNewModalPendingDeclarations = action.payload;
		},
		setShowNewModalPendingDeclarationWithPrecalculate: (state, action) => {
			state.showNewModalPendingDeclarationWithPrecalculate = action.payload;
		},
		setSelectedApprovalDeclaration: (state, action) => {
			state.selectedApprovalDeclaration = action.payload;
		},
		setShowRegularizationFlow: (state, action) => {
			state.showRegularizationFlow = action.payload;
			if (!action.payload) {
				state.regularizationFlowSource = null;
				state.regularizationFlowArm = null;
				state.regularizationFlowScope = 'monthly';
			}
		},
		/** Abre el flujo diciendo de dónde viene, y con qué brazo si lo forzaron. */
		openRegularizationFlow: (
			state,
			action: {
				payload: {
					source: string;
					arm?: string | null;
					scope?: 'monthly' | 'annual';
				};
			},
		) => {
			state.showRegularizationFlow = true;
			state.regularizationFlowSource = action.payload.source;
			state.regularizationFlowArm = action.payload.arm ?? null;
			state.regularizationFlowScope = action.payload.scope ?? 'monthly';
		},
		setPonteAlDiaArm: (state, action: { payload: string | null }) => {
			state.ponteAlDiaArm = action.payload;
		},
		setTaxpayer: (state, action) => {
			state.taxpayer = action.payload;
		},
		setIsLoadingTaxpayer: (state, action) => {
			state.isLoadingTaxpayer = action.payload;
		},
		setRetentionCertificates: (state, action) => {
			state.retentionCertificates = action.payload;
		},
		setRetentionCertificatesMeta: (state, action) => {
			state.retentionCertificatesMeta = action.payload;
		},
		setWrappedData: (state, action) => {
			state.wrappedData = action.payload;
		},
	},
});

export const {
	setIsLoadingTaxProfile,
	addLinks,
	restartLinks,
	addUserHasRetentions,
	setDeclarationsOverview,
	setIsLoadingDeclarations,
	addAnnualDeclarations,
	setIsLoadingAnnualDeclarations,
	setSelectedDeclaration,
	setShowNewModalPendingDeclarations,
	setShowNewModalPendingDeclarationWithPrecalculate,
	setSelectedApprovalDeclaration,
	setShowRegularizationFlow,
	openRegularizationFlow,
	setPonteAlDiaArm,
	setTaxpayer,
	setIsLoadingTaxpayer,
	setRetentionCertificates,
	setRetentionCertificatesMeta,
	setWrappedData,
} = taxProfileSlice.actions;

export default taxProfileSlice.reducer;
